import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader, Btn, Input, Select, Textarea, Modal, Card, Badge, EmptyState, fmtMoney, fmtDate } from "@/components/ui-kit";
import { Plus, FileSpreadsheet, FileText as FilePdf, Trash2, RotateCcw } from "lucide-react";

export default function Orders() {
  const { t } = useLang();
  const [orders, setOrders] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ pharmacy_id: "", payment_terms: "credit", notes: "", items: [] });
  const [busy, setBusy] = useState(false);
  const [returnOpen, setReturnOpen] = useState(null); // order being returned
  const [returnItems, setReturnItems] = useState([]);

  const load = async () => {
    const [o, p, pr] = await Promise.all([api.get("/orders"), api.get("/pharmacies"), api.get("/products")]);
    setOrders(o.data); setPharmacies(p.data); setProducts(pr.data);
  };
  useEffect(() => { load(); }, []);

  const addItem = () => setForm({ ...form, items: [...form.items, { product_id: "", product_name: "", quantity: 1, unit_price: 0, bonus_qty: 0, discount_pct: 0, total: 0 }]});
  const updateItem = (i, key, val) => {
    const items = [...form.items];
    items[i] = { ...items[i], [key]: val };
    if (key === "product_id") {
      const prod = products.find(p => p.id === val);
      if (prod) { items[i].product_name = prod.name; items[i].unit_price = prod.pharmacy_price; }
    }
    items[i].total = (items[i].quantity || 0) * (items[i].unit_price || 0) * (1 - (items[i].discount_pct || 0) / 100);
    setForm({ ...form, items });
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const subtotal = form.items.reduce((s, i) => s + (i.total || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (form.items.length === 0) { alert("Add at least one item"); return; }
    setBusy(true);
    try {
      const pharmacy = pharmacies.find(p => p.id === form.pharmacy_id);
      const payload = { ...form, pharmacy_name: pharmacy?.name };
      await api.post("/orders", payload);
      setOpen(false);
      setForm({ pharmacy_id: "", payment_terms: "credit", notes: "", items: [] });
      await load();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const updateStatus = async (id, status) => {
    if (status === "cancelled" && !window.confirm("Cancel order? Stock will be restored.")) return;
    await api.put(`/orders/${id}/status`, { status });
    await load();
  };

  const startReturn = (order) => {
    setReturnOpen(order);
    setReturnItems(order.items.map(i => ({ ...i, return_qty: 0, reason: "" })));
  };

  const submitReturn = async (e) => {
    e.preventDefault();
    const items = returnItems
      .filter(i => i.return_qty > 0)
      .map(i => ({
        product_id: i.product_id,
        product_name: i.product_name,
        quantity: i.return_qty,
        unit_price: i.unit_price,
        total: i.return_qty * i.unit_price,
        reason: i.reason,
      }));
    if (items.length === 0) { alert("Select at least one item"); return; }
    await api.post(`/orders/${returnOpen.id}/returns`, { order_id: returnOpen.id, items });
    setReturnOpen(null); setReturnItems([]);
    await load();
  };

  return (
    <div data-testid="orders-page">
      <PageHeader
        title={t("orders")}
        subtitle="Sales & distribution invoices"
        actions={
          <>
            <Btn variant="secondary" onClick={() => window.open(`${API_BASE}/export/orders/excel`)}><FileSpreadsheet className="w-4 h-4"/>Excel</Btn>
            <Btn variant="secondary" onClick={() => window.open(`${API_BASE}/export/orders/pdf`)}><FilePdf className="w-4 h-4"/>PDF</Btn>
            <Btn onClick={() => setOpen(true)} data-testid="add-order-btn"><Plus className="w-4 h-4"/>{t("add")}</Btn>
          </>
        }
      />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="py-2 px-3 text-start">{t("invoice")}</th>
                <th className="py-2 px-3 text-start">{t("pharmacy")}</th>
                <th className="py-2 px-3 text-start">{t("rep")}</th>
                <th className="py-2 px-3 text-start">Items</th>
                <th className="py-2 px-3 text-end">{t("total")}</th>
                <th className="py-2 px-3 text-end">Paid</th>
                <th className="py-2 px-3 text-start">{t("status")}</th>
                <th className="py-2 px-3 text-start">{t("paymentStatus")}</th>
                <th className="py-2 px-3 text-start">{t("date")}</th>
                <th className="py-2 px-3 text-end">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3 font-mono text-xs font-semibold">{o.invoice_number}</td>
                  <td className="py-2 px-3 font-semibold text-slate-900">{o.pharmacy_name}</td>
                  <td className="py-2 px-3 text-slate-700">{o.rep_name}</td>
                  <td className="py-2 px-3 text-slate-600 text-xs">{o.items.length} item(s)</td>
                  <td className="py-2 px-3 text-end font-bold text-blue-700">{fmtMoney(o.total)}</td>
                  <td className="py-2 px-3 text-end text-emerald-700">{fmtMoney(o.paid_amount)}</td>
                  <td className="py-2 px-3">
                    <select value={o.status} onChange={(e) => updateStatus(o.id, e.target.value)} className="text-xs border border-slate-200 rounded px-1 py-0.5">
                      <option value="pending">pending</option><option value="delivered">delivered</option><option value="cancelled">cancelled</option>
                    </select>
                  </td>
                  <td className="py-2 px-3"><Badge color={o.payment_status === "paid" ? "green" : o.payment_status === "partial" ? "amber" : "red"}>{o.payment_status}</Badge></td>
                  <td className="py-2 px-3 text-xs">{fmtDate(o.created_at)}</td>
                  <td className="py-2 px-3 text-end">
                    {o.status !== "cancelled" && (
                      <button onClick={() => startReturn(o)} className="text-xs text-amber-700 hover:bg-amber-50 px-2 py-1 rounded inline-flex items-center gap-1" data-testid={`return-${o.id}`}>
                        <RotateCcw className="w-3 h-3"/>Return
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {orders.length === 0 && <EmptyState message={t("noData")}/>}
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="New Order" maxWidth="max-w-3xl">
        <form onSubmit={submit} className="space-y-4" data-testid="order-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select label="Pharmacy *" required value={form.pharmacy_id} onChange={(e) => setForm({...form, pharmacy_id: e.target.value})} data-testid="order-pharmacy">
              <option value="">--Select--</option>
              {pharmacies.map(p => <option key={p.id} value={p.id}>{p.name} ({p.city})</option>)}
            </Select>
            <Select label="Payment Terms" value={form.payment_terms} onChange={(e) => setForm({...form, payment_terms: e.target.value})}>
              <option value="cash">Cash</option><option value="credit">Credit</option>
            </Select>
            <Input label="Delivery Date" type="date" value={form.delivery_date || ""} onChange={(e) => setForm({...form, delivery_date: e.target.value})}/>
          </div>

          <div className="border-t border-slate-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Items</div>
              <Btn type="button" variant="secondary" onClick={addItem} data-testid="add-item-btn"><Plus className="w-3 h-3"/>Add Item</Btn>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 bg-slate-50 rounded border border-slate-200">
                  <div className="col-span-12 md:col-span-4">
                    <Select label={i === 0 ? "Product" : ""} value={it.product_id} onChange={(e) => updateItem(i, "product_id", e.target.value)}>
                      <option value="">--</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </Select>
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <Input label={i === 0 ? "Qty" : ""} type="number" value={it.quantity} onChange={(e) => updateItem(i, "quantity", Number(e.target.value))}/>
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <Input label={i === 0 ? "Price" : ""} type="number" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", Number(e.target.value))}/>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Input label={i === 0 ? "Bonus" : ""} type="number" value={it.bonus_qty} onChange={(e) => updateItem(i, "bonus_qty", Number(e.target.value))}/>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Input label={i === 0 ? "Disc%" : ""} type="number" value={it.discount_pct} onChange={(e) => updateItem(i, "discount_pct", Number(e.target.value))}/>
                  </div>
                  <div className="col-span-2 md:col-span-1 text-end font-bold text-blue-700">{fmtMoney(it.total)}</div>
                  <button type="button" onClick={() => removeItem(i)} className="col-span-1 p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4"/></button>
                </div>
              ))}
              {form.items.length === 0 && <div className="text-sm text-slate-400 text-center py-4">No items added</div>}
            </div>
            <div className="flex justify-end mt-3 pt-3 border-t border-slate-200">
              <div className="text-end">
                <div className="overline">Subtotal</div>
                <div className="text-2xl font-extrabold text-blue-700">{fmtMoney(subtotal)} EGP</div>
              </div>
            </div>
          </div>
          <Textarea label="Notes" rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
          <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>{t("cancel")}</Btn>
            <Btn type="submit" disabled={busy} data-testid="order-submit">{busy ? t("loading") : "Create Order"}</Btn>
          </div>
        </form>
      </Modal>

      <Modal open={!!returnOpen} onClose={() => setReturnOpen(null)} title={`Return Items · ${returnOpen?.invoice_number || ""}`} maxWidth="max-w-2xl">
        {returnOpen && (
          <form onSubmit={submitReturn} className="space-y-3">
            <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
              Set return quantities below. Stock will be restored automatically and a credit note will be generated.
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-slate-500 border-b">
                  <th className="text-start py-2">Product</th>
                  <th className="text-end py-2">Ordered</th>
                  <th className="text-center py-2">Return Qty</th>
                  <th className="text-start py-2">Reason</th>
                  <th className="text-end py-2">Refund</th>
                </tr>
              </thead>
              <tbody>
                {returnItems.map((it, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-2 font-semibold">{it.product_name}</td>
                    <td className="py-2 text-end">{it.quantity}</td>
                    <td className="py-2 text-center">
                      <input type="number" min="0" max={it.quantity} value={it.return_qty}
                        onChange={(e) => { const arr = [...returnItems]; arr[i].return_qty = Math.min(Number(e.target.value), it.quantity); setReturnItems(arr); }}
                        className="w-20 rounded border border-slate-300 px-2 py-1 text-center"/>
                    </td>
                    <td className="py-2">
                      <input value={it.reason} onChange={(e) => { const arr = [...returnItems]; arr[i].reason = e.target.value; setReturnItems(arr); }}
                        placeholder="Optional" className="w-full rounded border border-slate-300 px-2 py-1 text-xs"/>
                    </td>
                    <td className="py-2 text-end font-bold text-amber-700">{fmtMoney((it.return_qty || 0) * it.unit_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Btn variant="ghost" type="button" onClick={() => setReturnOpen(null)}>{t("cancel")}</Btn>
              <Btn variant="danger" type="submit" data-testid="confirm-return">Process Return</Btn>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
